import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Engine and Gearbox Protection
  FaFileInvoiceDollar, // For Return to Invoice
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaCarSide, // For Tyre and Rim Protection
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const MercedesBenzCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Are zero depreciation policies recommended for Mercedes-Benz?",
      answer:
        "Yes, they are highly recommended, especially for newer models and during the first 5 years.",
    },
    {
      question:
        "What’s the average claim settlement time for luxury car insurance?",
      answer:
        "Typically between 7 to 14 working days, depending on documentation and insurer process.",
    },
    {
      question: "Can I insure a used Mercedes-Benz?",
      answer:
        "Yes, used Mercedes cars can be insured after a pre-inspection by the insurer.",
    },
    {
      question:
        "Is Return to Invoice a necessary add-on for Mercedes-Benz cars?",
      answer:
        "Yes, particularly important for new cars above ₹50 lakh to cover total loss or theft.",
    },
    {
      question: "Are Mercedes-Benz electric cars like EQB and EQS covered?",
      answer:
        "Yes, insurers now offer EV-specific policies with battery and charger cover.",
    },
    {
      question: "Does driving history affect insurance rates?",
      answer: "Yes, a clean driving record helps reduce your premium.",
    },
    {
      question: "Can I get cashless repair at Mercedes-authorized garages?",
      answer:
        "Yes, most luxury-focused insurers offer cashless facilities at authorized service centers.",
    },
    {
      question: "What is the fine for driving without insurance?",
      answer:
        "₹2,000 for the first offence and ₹4,000 for subsequent violations.",
    },
    {
      question: "How is IDV calculated for Mercedes-Benz cars?",
      answer: "Based on ex-showroom value minus depreciation as per model age.",
    },
    {
      question: "Can I transfer NCB from my previous luxury car?",
      answer: "Yes, provided the new car is in the same name.",
    },
    {
      question: "What happens to insurance if I sell my Mercedes-Benz?",
      answer:
        "It can be transferred to the new buyer or canceled for a refund.",
    },
    {
      question: "Is engine protection add-on really needed?",
      answer:
        "Yes, especially in urban areas prone to flooding or with heavy usage.",
    },
    {
      question: "Are hybrid Mercedes models treated differently in insurance?",
      answer:
        "Some insurers offer specific terms for hybrids, often similar to petrol models.",
    },
    {
      question: "Can I insure performance models like AMG variants?",
      answer:
        "Yes, although premiums will be higher and some add-ons may be mandatory.",
    },
    {
      question: "Is insurance renewal online or offline better?",
      answer:
        "Online renewals are faster, but offline may be useful for high-value discussions.",
    },
    {
      question: "Does policy lapse affect my NCB?",
      answer: "Yes, a lapse beyond 90 days usually resets your NCB benefits.",
    },
    {
      question: "Are cosmetic damages covered in comprehensive plans?",
      answer:
        "Only if caused by an accident. Optional covers may be needed for minor scratches.",
    },
    {
      question: "Do I get special policies if I own multiple Mercedes cars?",
      answer:
        "Some insurers offer fleet discounts or bundled policies for multi-car households.",
    },
    {
      question: "How often should I review my Mercedes insurance policy?",
      answer: "At least once a year, or if you modify or upgrade your car.",
    },
    {
      question: "What is the best add-on for city driving?",
      answer:
        "Roadside Assistance and Engine Protection are valuable in city traffic and conditions.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation",
      description:
        "Ensures full claim value without deduction for parts like bumpers or alloy wheels.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description:
        "Highly useful for newly purchased models, especially high-value variants like the S-Class or EQS.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description:
        "Important in metro areas prone to waterlogging or uneven road conditions.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Covers expensive fluids, filters, and lubricants used in repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description:
        "Includes flatbed towing, emergency fuel delivery and on-site repair.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Tyre and Rim Protection",
      description: "Ideal for performance tires and expensive alloy wheels.",
      icon: <FaCarSide className="text-4xl text-indigo-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];
  const mercedesinsurance = [
    {
      name: "GLA",
      path: "/mercedes-benz-gla-car-insurance",
      image: "/assets/mercedesbenzgla.jpeg",
    },
      {
      name: "GLC",
      path: "/mercedes-benz-glc-car-insurance",
      image: "/assets/mercedesbenzglc.jpeg",
    },
       {
      name: "GLE",
      path: "/mercedes-benz-gle-car-insurance",
      image: "/assets/mercedesbenzgle.jpeg",
    },
      {
      name: "GLS",
      path: "/mercedes-benz-gls-car-insurance",
      image: "/assets/mercedesbenzgls.jpeg",
    },
     {
      name: "A-Class Limousine",
      path: "/mercedes-benz-aclass-limousine-car-insurance",
      image: "/assets/mercedesbenzaclass.jpeg",
    },
     {
      name: "C-Class ",
      path: "/mercedes-benz-cclass-car-insurance",
      image: "/assets/mercedesbenzcclass.jpeg",
    },
    {
      name: "E-Class ",
      path: "/mercedes-benz-eclass-car-insurance",
      image: "/assets/mercedesbenzeclass.jpeg",
    },
    {
      name: "S-Class ",
      path: "/mercedes-benz-sclass-car-insurance",
      image: "/assets/mercedesbenzsclass.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Mercedes-Benz Car Insurance in India – Compare Quotes & Buy Online
        </title>
        <meta
          name="description"
          content="Explore Mercedes-Benz car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Mercedes-Benz vehicle."
        />
        <meta
          name="keywords"
          content="mercedes-benz car insurance, luxury car insurance india, mercedes c-class insurance, mercedes gla insurance, mercedes eqb insurance, car insurance quotes"
        />
          <link
          rel="canonical"
          href="https://thefinmaster.com/mercedes-benz-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
       
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Mercedes-Benz Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Mercedes-Benz Car Insurance
                  </span>{" "}
                  
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                  Mercedes-Benz is synonymous with luxury, performance, and
                  engineering excellence. Whether you drive a sleek C-Class or a
                  commanding GLE, protecting your prized vehicle begins with the
                  right insurance coverage. This guide takes you through
                  everything you need to know about insuring a Mercedes-Benz,
                  from average premiums and model insights to useful add-ons,
                  renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/mercedesinsuranc.jpeg"
                alt="Mercedes-Benz Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Mercedes-Benz in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Mercedes-Benz has established itself as the leading luxury car brand
            in India. The German automaker offers a diverse range of models
            catering to executives, entrepreneurs and enthusiasts who value
            refinement, safety and performance.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Popular models include the A-Class Limousine, C-Class, GLA, GLC and
            top-of-the-line S-Class and EQB electric vehicles. Whether you use
            your Mercedes for city commutes or weekend drives, protecting it
            with a tailored insurance plan is essential.
          </p>
        </div>
      </div>
      <div className="p-4 sm:p-8 bg-white">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
          Choose Your Car Brand
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {mercedesinsurance.map((calc, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.04 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Link
                to={calc.path}
                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
              >
                <img
                  src={calc.image}
                  alt={calc.name}
                  className="w-16 h-16 object-contain mb-2"
                />
                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                  {calc.name}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Mercedes-Benz Owners Need Robust Insurance Coverage
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Mercedes-Benz cars feature advanced technologies such as
            radar-assisted safety, premium infotainment, ADAS systems and
            performance-tuned engines. Repair and part replacement costs can be
            significantly higher than standard vehicles. A comprehensive
            insurance policy safeguards your investment from:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Road accidents and accidental damage</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft and vandalism</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Fire and natural calamities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party liabilities and legal expenses</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Expensive bodywork and electronic repairs</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            Mercedes-Benz cars are also serviced at premium garages. Having
            insurance ensures access to authorized workshops, genuine parts, and
            hassle-free claims.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Mercedes-Benz Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s an overview of popular Mercedes-Benz cars in India, their
          approximate Insured Declared Value (IDV) and starting insurance
          premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "A-Class Limousine",
                category: "Entry-level Luxury",
                idv: "45–55 Lakh",
                premium: "₹35,000 to ₹45,000",
              },
              {
                model: "GLA",
                category: "Compact SUV",
                idv: "50–65 Lakh",
                premium: "₹40,000 to ₹52,000",
              },
              {
                model: "C-Class",
                category: "Luxury Sedan",
                idv: "60–75 Lakh",
                premium: "₹42,000 to ₹58,000",
              },
              {
                model: "GLC",
                category: "Mid-size SUV",
                idv: "75–90 Lakh",
                premium: "₹50,000 to ₹65,000",
              },
              {
                model: "E-Class",
                category: "Executive Sedan",
                idv: "85 Lakh–1 Cr",
                premium: "₹55,000 to ₹70,000",
              },
              {
                model: "GLE",
                category: "Premium SUV",
                idv: "1 Cr–1.2 Cr",
                premium: "₹65,000 to ₹85,000",
              },
              {
                model: "S-Class",
                category: "Flagship Luxury",
                idv: "1.5 Cr–2 Cr",
                premium: "₹90,000 to ₹1.2 Lakh",
              },
              {
                model: "EQB / EQS",
                category: "Electric Range",
                idv: "75 Lakh–2.5 Cr",
                premium: "₹65,000 to ₹1.3 Lakh",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Category
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "A-Class Limousine",
                    "Entry-level Luxury",
                    "45–55 Lakh",
                    "₹35,000 to ₹45,000",
                  ],
                  ["GLA", "Compact SUV", "50–65 Lakh", "₹40,000 to ₹52,000"],
                  [
                    "C-Class",
                    "Luxury Sedan",
                    "60–75 Lakh",
                    "₹42,000 to ₹58,000",
                  ],
                  ["GLC", "Mid-size SUV", "75–90 Lakh", "₹50,000 to ₹65,000"],
                  [
                    "E-Class",
                    "Executive Sedan",
                    "85 Lakh–1 Cr",
                    "₹55,000 to ₹70,000",
                  ],
                  ["GLE", "Premium SUV", "1 Cr–1.2 Cr", "₹65,000 to ₹85,000"],
                  [
                    "S-Class",
                    "Flagship Luxury",
                    "1.5 Cr–2 Cr",
                    "₹90,000 to ₹1.2 Lakh",
                  ],
                  [
                    "EQB / EQS",
                    "Electric Range",
                    "75 Lakh–2.5 Cr",
                    "₹65,000 to ₹1.3 Lakh",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for Mercedes-Benz Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Mercedes-Benz cars come with cutting-edge features and are often used
          in premium settings. The following add-ons can provide extra peace of
          mind:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurance Companies for Mercedes-Benz Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here are some trusted insurers that offer specialized plans and
          excellent claim service for luxury vehicles:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              HDFC ERGO
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Offers premium car plans with wide cashless garage access.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for smooth digital claims and high-value car coverage.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Tata AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Customizable luxury car policies with premium add-ons.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Efficient claims and plans tailored for luxury car owners.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">
              Good digital experience and fast issuance for luxury and electric
              vehicles.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Reduce Mercedes-Benz Insurance Premiums
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Owning a luxury car doesn’t mean you can’t optimize your premium.
              Try these steps to save smartly:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare quotes from multiple insurers annually.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Transfer and retain your No Claim Bonus (NCB).</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Choose a higher voluntary deductible if you’re a safe driver.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Avoid small claims to preserve long-term discounts.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install certified security systems or GPS trackers.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Needed for Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Whether you’re buying a new policy or renewing your current one,
              keep these documents ready:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>RC copy of your Mercedes-Benz car</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance policy details</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>PAN and Aadhaar</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid PUC certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-sized photos (if buying offline)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Mercedes-Benz Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                A Mercedes-Benz represents luxury, comfort and engineering
                excellence. Protecting it with a strong insurance plan ensures
                you continue enjoying every drive without worry. Whether you own
                a sleek C-Class or an electric EQB, the right insurance lets you
                drive with peace of mind.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare policies, choose the best
                add-ons and ensure your Mercedes-Benz is protected at every
                stage.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default MercedesBenzCarInsurance;
